import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Table, Modal, Form, Input, Select, message, Tag, Checkbox, Popconfirm, Divider, Drawer } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined, SnippetsOutlined, UserOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;
const { TextArea } = Input;

const CourseManagement = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [videos, setVideos] = useState([]);
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courseModalVisible, setCourseModalVisible] = useState(false);
  const [videoDrawerVisible, setVideoDrawerVisible] = useState(false);
  const [addVideoModalVisible, setAddVideoModalVisible] = useState(false);
  const [selectVideoModalVisible, setSelectVideoModalVisible] = useState(false);
  const [departmentModalVisible, setDepartmentModalVisible] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [videoSearch, setVideoSearch] = useState('');
  const [courseForm] = Form.useForm();
  const [videoForm] = Form.useForm();

  useEffect(() => {
    fetchCourses();
    fetchVideos();
    fetchPositions();
    fetchDepartments();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await api.get('/learning/courses');
      if (response.data && response.data.success) {
        const coursesData = response.data.data.courses || [];
        setCourses(coursesData);
        return coursesData;
      }
      return [];
    } catch (error) {
      message.error('获取课程失败');
      console.error('获取课程失败:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await api.get('/learning/videos');
      if (response.data && response.data.success) {
        setVideos(response.data.data.videos || []);
      }
    } catch (error) {
      message.error('获取视频失败');
      console.error('获取视频失败:', error);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await api.get('/positions');
      if (response.data && response.data.success) {
        setPositions(response.data.data || []);
      }
    } catch (error) {
      message.error('获取岗位失败');
      console.error('获取岗位失败:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      if (response.data && response.data.success) {
        setDepartments(response.data.data || []);
      }
    } catch (error) {
      message.error('获取部门失败');
      console.error('获取部门失败:', error);
    }
  };

  const handleCourseCreate = () => {
    setCurrentCourse(null);
    courseForm.resetFields();
    setCourseModalVisible(true);
  };

  const handleCourseEdit = (course) => {
    setCurrentCourse(course);
    courseForm.setFieldsValue({
      name: course.name,
      type: course.type,
      description: course.description
    });
    setCourseModalVisible(true);
  };

  const handleCourseDelete = async (id) => {
    try {
      await api.delete(`/learning/courses/${id}`);
      message.success('删除课程成功');
      fetchCourses();
    } catch (error) {
      message.error('删除课程失败');
      console.error('删除课程失败:', error);
    }
  };

  const handleCourseSubmit = async (values) => {
    try {
      if (currentCourse) {
        await api.put(`/learning/courses/${currentCourse.id}`, values);
        message.success('更新课程成功');
      } else {
        await api.post('/learning/courses', values);
        message.success('创建课程成功');
      }
      setCourseModalVisible(false);
      fetchCourses();
    } catch (error) {
      message.error('保存课程失败');
      console.error('保存课程失败:', error);
    }
  };

  const handleAddVideoToCourse = async (courseId, videoId) => {
    try {
      await api.post(`/learning/courses/${courseId}/videos`, { video_id: videoId });
      message.success('视频添加到课程成功');
      fetchCourses();
    } catch (error) {
      message.error('添加视频失败');
      console.error('添加视频失败:', error);
    }
  };

  const handleRemoveVideoFromCourse = async (courseId, videoId) => {
    try {
      await api.delete(`/learning/courses/${courseId}/videos/${videoId}`);
      message.success('视频从课程移除成功');
      // 重新获取课程列表并更新currentCourse状态
      fetchCourses().then((updatedCourses) => {
        if (currentCourse) {
          const updatedCourse = updatedCourses.find(c => c.id === currentCourse.id);
          if (updatedCourse) {
            setCurrentCourse(updatedCourse);
          }
        }
      });
    } catch (error) {
      message.error('移除视频失败');
      console.error('移除视频失败:', error);
    }
  };

  const handleBindPositionToCourse = async (courseId, positionId) => {
    try {
      await api.post(`/learning/courses/${courseId}/positions`, { position_id: positionId });
      message.success('课程绑定岗位成功');
      fetchCourses();
    } catch (error) {
      message.error('绑定岗位失败');
      console.error('绑定岗位失败:', error);
    }
  };

  const handleUnbindPositionFromCourse = async (courseId, positionId) => {
    try {
      await api.delete(`/learning/courses/${courseId}/positions/${positionId}`);
      message.success('解除岗位绑定成功');
      fetchCourses();
    } catch (error) {
      message.error('解除绑定失败');
      console.error('解除绑定失败:', error);
    }
  };

  const handleAddVideoModal = (course) => {
    setCurrentCourse(course);
    setVideoDrawerVisible(true);
  };

  const handleSelectVideoModal = (course) => {
    setCurrentCourse(course);
    // 初始化已选择的视频
    const alreadyAddedVideoIds = course.videos ? course.videos.map(v => v.id) : [];
    setSelectedVideos(alreadyAddedVideoIds);
    setSelectVideoModalVisible(true);
  };

  const handleSelectVideoSubmit = async () => {
    if (!currentCourse) return;
    
    try {
      // 先移除所有已添加的视频
      const currentVideoIds = currentCourse.videos ? currentCourse.videos.map(v => v.id) : [];
      for (const videoId of currentVideoIds) {
        if (!selectedVideos.includes(videoId)) {
          await api.delete(`/learning/courses/${currentCourse.id}/videos/${videoId}`);
        }
      }
      
      // 统计需要添加的视频数量
      const videosToAdd = selectedVideos.filter(videoId => !currentVideoIds.includes(videoId));
      console.log('需要添加的视频:', videosToAdd);
      
      if (videosToAdd.length === 0) {
        message.info('没有选择新视频');
        setSelectVideoModalVisible(false);
        return;
      }
      
      // 添加新选择的视频
      let successCount = 0;
      for (const videoId of videosToAdd) {
        try {
          console.log('添加视频:', { course_id: currentCourse.id, video_id: videoId });
          const response = await api.post(`/learning/courses/${currentCourse.id}/videos`, { video_id: videoId });
          console.log('添加视频响应:', response.data);
          if (response.data && response.data.success) {
            successCount++;
          } else {
            console.error('添加视频失败:', response.data);
          }
        } catch (error) {
          console.error('添加视频失败:', error);
        }
      }
      
      if (successCount > 0) {
        message.success(`成功添加 ${successCount} 个视频`);
        // 重新获取课程信息，更新抽屉中的视频列表
        fetchCourses().then((updatedCourses) => {
          // 找到更新后的课程
          const updatedCourse = updatedCourses.find(c => c.id === currentCourse.id);
          if (updatedCourse) {
            setCurrentCourse(updatedCourse);
            console.log('更新后的课程视频:', updatedCourse.videos);
          }
        });
      } else {
        message.error('添加视频失败');
      }
      
      setSelectVideoModalVisible(false);
    } catch (error) {
      message.error('添加视频失败');
      console.error('添加视频失败:', error);
    }
  };

  const handleBindDepartmentModal = (course) => {
    setCurrentCourse(course);
    // 初始化已选择的部门
    const alreadyBoundDepartmentIds = course.departments ? course.departments.map(d => d.id) : [];
    setSelectedDepartments(alreadyBoundDepartmentIds);
    setDepartmentModalVisible(true);
  };

  const handleBindDepartmentSubmit = async () => {
    if (!currentCourse) return;
    
    try {
      // 先移除所有已绑定的部门
      const currentDepartmentIds = currentCourse.departments ? currentCourse.departments.map(d => d.id) : [];
      for (const deptId of currentDepartmentIds) {
        if (!selectedDepartments.includes(deptId)) {
          await api.delete(`/learning/courses/${currentCourse.id}/departments/${deptId}`);
        }
      }
      
      // 添加新选择的部门
      for (const deptId of selectedDepartments) {
        if (!currentDepartmentIds.includes(deptId)) {
          await api.post(`/learning/courses/${currentCourse.id}/departments`, { department_id: deptId });
        }
      }
      
      message.success('部门绑定成功');
      setDepartmentModalVisible(false);
      fetchCourses();
    } catch (error) {
      message.error('绑定部门失败');
      console.error('绑定部门失败:', error);
    }
  };

  const handleAddNewVideo = (course) => {
    setCurrentCourse(course);
    videoForm.resetFields();
    setAddVideoModalVisible(true);
  };

  const handleAddNewVideoSubmit = async (values) => {
    if (!currentCourse) return;
    
    try {
      // 先创建视频
      const videoResponse = await api.post('/learning/videos', values);
      if (videoResponse.data && videoResponse.data.success) {
        const videoId = videoResponse.data.data.id;
        
        // 然后将视频绑定到当前课程
        await api.post(`/learning/courses/${currentCourse.id}/videos`, { video_id: videoId });
        
        message.success('视频添加成功并绑定到课程');
        setAddVideoModalVisible(false);
        fetchCourses();
        fetchVideos();
      }
    } catch (error) {
      message.error('添加视频失败');
      console.error('添加视频失败:', error);
    }
  };

  const courseColumns = [
    {
      title: '课程名称',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span style={{ fontWeight: 'bold' }}>{name}</span>
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === '管理课' ? 'blue' : 'green'}>
          {type}
        </Tag>
      )
    },
    {
      title: '视频数量',
      key: 'videoCount',
      render: (_, record) => record.videos ? record.videos.length : 0
    },
    {
      title: '绑定岗位',
      key: 'positionCount',
      render: (_, record) => record.positions ? record.positions.length : 0
    },
    {
      title: '绑定部门',
      key: 'departmentCount',
      render: (_, record) => record.departments ? record.departments.length : 0
    },
    {
      title: '创建人',
      dataIndex: 'created_by_name',
      key: 'created_by_name'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt) => new Date(createdAt).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleCourseEdit(record)} />
          <Button type="text" icon={<PlayCircleOutlined />} onClick={() => handleAddVideoModal(record)}>视频管理</Button>
          <Button type="text" icon={<UserOutlined />} onClick={() => handleBindDepartmentModal(record)}>部门绑定</Button>
          <Popconfirm
            title="确定要删除这个课程吗？"
            onConfirm={() => handleCourseDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="course-management-container" style={{ padding: '10px 16px' }}>
      <div className="course-management-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '24px', margin: 0 }}>课程管理</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCourseCreate}>
          创建课程
        </Button>
      </div>

      {/* 课程管理 */}
      <Card style={{ marginBottom: '20px' }} title={
        <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>课程列表</span>
          <Button icon={<ReloadOutlined />} onClick={fetchCourses} loading={loading}>
            刷新
          </Button>
        </Space>
      }>
        <Table
          columns={courseColumns}
          dataSource={courses}
          rowKey="id"
          loading={loading}
        />
      </Card>

      {/* 课程模态框 */}
      <Modal
        title={currentCourse ? '编辑课程' : '创建课程'}
        open={courseModalVisible}
        onCancel={() => setCourseModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={courseForm}
          onFinish={handleCourseSubmit}
          layout="vertical"
        >
          <Form.Item
            label="课程名称"
            name="name"
            rules={[{ required: true, message: '请输入课程名称' }]}
          >
            <Input placeholder="请输入课程名称" />
          </Form.Item>

          <Form.Item
            label="课程类型"
            name="type"
            rules={[{ required: true, message: '请选择课程类型' }]}
          >
            <Select placeholder="请选择课程类型">
              <Option value="管理课">管理课</Option>
              <Option value="技能课">技能课</Option>
              <Option value="其他">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="课程描述"
            name="description"
          >
            <TextArea rows={4} placeholder="请输入课程描述" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setCourseModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 视频管理抽屉 */}
      <Drawer
        title={`${currentCourse?.name || ''} - 视频管理`}
        placement="right"
        onClose={() => setVideoDrawerVisible(false)}
        open={videoDrawerVisible}
        width={600}
      >
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>已添加的视频</h3>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleSelectVideoModal(currentCourse)}>
            添加已有视频
          </Button>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Button type="default" icon={<PlusOutlined />} onClick={() => handleAddNewVideo(currentCourse)}>
            上传新视频
          </Button>
        </div>
        <Table
          columns={[
            {
              title: '视频名称',
              dataIndex: 'name',
              key: 'name'
            },
            {
              title: '操作',
              key: 'action',
              render: (_, record) => (
                <Button 
                  type="text" 
                  danger 
                  onClick={() => handleRemoveVideoFromCourse(currentCourse.id, record.id)}
                >
                  移除
                </Button>
              )
            }
          ]}
          dataSource={currentCourse?.videos || []}
          rowKey="id"
          pagination={false}
        />
      </Drawer>

      {/* 选择已有视频模态框 */}
      <Modal
        title="选择已有视频"
        open={selectVideoModalVisible}
        onCancel={() => {
          setSelectVideoModalVisible(false);
          setVideoSearch('');
        }}
        onOk={handleSelectVideoSubmit}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="搜索视频名称"
            value={videoSearch}
            onChange={(e) => setVideoSearch(e.target.value)}
          />
        </div>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {videos
            .filter(video => video.name.toLowerCase().includes(videoSearch.toLowerCase()))
            .map(video => (
              <Checkbox
                key={video.id}
                checked={selectedVideos.includes(video.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedVideos([...selectedVideos, video.id]);
                  } else {
                    setSelectedVideos(selectedVideos.filter(id => id !== video.id));
                  }
                }}
              >
                {video.name}
              </Checkbox>
            ))}
        </div>
      </Modal>

      {/* 部门选择模态框 */}
      <Modal
        title="选择部门"
        open={departmentModalVisible}
        onCancel={() => setDepartmentModalVisible(false)}
        onOk={handleBindDepartmentSubmit}
        width={600}
      >
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {departments.map(dept => (
            <Checkbox
              key={dept.id}
              checked={selectedDepartments.includes(dept.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedDepartments([...selectedDepartments, dept.id]);
                } else {
                  setSelectedDepartments(selectedDepartments.filter(id => id !== dept.id));
                }
              }}
            >
              {dept.name}
            </Checkbox>
          ))}
        </div>
      </Modal>

      {/* 添加视频模态框 */}
      <Modal
        title="添加视频"
        open={addVideoModalVisible}
        onCancel={() => setAddVideoModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={videoForm}
          onFinish={handleAddNewVideoSubmit}
          layout="vertical"
        >
          <Form.Item
            label="视频名称"
            name="name"
            rules={[{ required: true, message: '请输入视频名称' }]}
          >
            <Input placeholder="请输入视频名称" />
          </Form.Item>

          <Form.Item
            label="视频链接"
            name="url"
            rules={[{ required: true, message: '请输入视频链接' }]}
          >
            <Input placeholder="请输入视频链接" />
          </Form.Item>

          <Form.Item
            label="视频类型"
            name="type"
            rules={[{ required: true, message: '请选择视频类型' }]}
          >
            <Select placeholder="请选择视频类型">
              <Option value="管理课">管理课</Option>
              <Option value="技能课">技能课</Option>
              <Option value="其他">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="视频描述"
            name="description"
          >
            <TextArea rows={4} placeholder="请输入视频描述" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setAddVideoModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CourseManagement;